import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WeekSection from '../components/WeeklyNews/WeekSection';
import Loading from '../components/Common/Loading';
import { PageErrorBoundary } from '../components/Common/ErrorBoundary';
import { WeeklySummaryData } from '../types/weekly';
import { weeklySummaryService } from '../services/weeklySummaryService';
import './Home.css';
import './Archives.css';

const Archives: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [weeklySummaries, setWeeklySummaries] = useState<WeeklySummaryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleWeekClick = (weeklySummary: WeeklySummaryData) => {
    navigate(`/archives/week/${weeklySummary.id}`);
  };

  useEffect(() => {
    const fetchWeeklySummaries = async () => {
      try {
        setLoading(true);
        console.log('Fetching weekly summaries...');
        
        // Fetch weekly summaries from database
        const data = await weeklySummaryService.getWeeklySummaries();
        console.log('Weekly summaries data:', data);
        
        setWeeklySummaries(data.data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching weekly summaries:', err);
        // Don't show error for empty state - show friendly message instead
        setWeeklySummaries([]);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchWeeklySummaries();
  }, []);

  const filteredSummaries = weeklySummaries.filter(summary => 
    summary.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    summary.summary.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <Loading />;
  if (error) return <div className="error-container"><h2>Error Loading Archives</h2><p>{error}</p></div>;

  return (
    <PageErrorBoundary>
      <div className="archives">
        <div className="archives__container">
          <div className="archives__header">
            <h1>Weekly Archives</h1>
            <p>Browse our comprehensive weekly summaries of AI business developments</p>
          </div>

          <main className="archives__content">
            <div className="archives__main">
              <section className="archives__section">
                <div className="archives-filters" style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <input
                    type="text"
                    placeholder="Search archives..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      fontSize: '16px', 
                      border: '1px solid #ddd', 
                      borderRadius: '6px',
                      outline: 'none'
                    }}
                  />
                </div>
              </section>

              <div className="archives-content">
                {filteredSummaries.length > 0 ? (
                  <div className="weekly-summaries">
                    {filteredSummaries.map((weeklySummary) => (
                      <section 
                        key={weeklySummary.id} 
                        className="archives__week-section"
                        onClick={() => handleWeekClick(weeklySummary)}
                        style={{ cursor: 'pointer' }}
                      >
                        <WeekSection
                          weeklySummary={weeklySummary}
                          dailySummaries={[]}
                          defaultExpanded={false}
                          showThemes={true}
                          showStats={true}
                          showDailySummaries={false}
                          onDayClick={() => {}}
                        />
                      </section>
                    ))}
                  </div>
                ) : (
                  <div className="no-archives">
                    <div className="no-archives__icon">📚</div>
                    {searchTerm ? (
                      <>
                        <h3 className="no-archives__title">No Archives Found</h3>
                        <p className="no-archives__text">
                          No weekly summaries match your search for "{searchTerm}".
                        </p>
                        <button 
                          onClick={() => setSearchTerm('')}
                          className="no-archives__button"
                        >
                          Clear Search
                        </button>
                      </>
                    ) : (
                      <>
                        <h3 className="no-archives__title">Weekly Archives Coming Soon</h3>
                        <p className="no-archives__text">
                          Weekly summaries are automatically generated every Sunday at 5 PM PST.<br/>
                          Check back soon for comprehensive weekly digests of AI business developments!
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </PageErrorBoundary>
  );
};

export default Archives;
